import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { supabase } from '../lib/supabase'
import { useSubscription } from '../lib/useSubscription'

export default function Dashboard() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const sub = useSubscription()

  const [loading, setLoading] = useState(true)
  const [userName, setUserName] = useState('')

  // This month
  const [monthReceiptCount, setMonthReceiptCount] = useState(0)
  const [monthReceiptTotal, setMonthReceiptTotal] = useState(0)
  const [monthMileage, setMonthMileage] = useState(0)
  const [monthMileageDeduction, setMonthMileageDeduction] = useState(0)
  const [monthCompanyCardTotal, setMonthCompanyCardTotal] = useState(0)
  const [monthCompanyCardCount, setMonthCompanyCardCount] = useState(0)
  const [monthReimbursableTotal, setMonthReimbursableTotal] = useState(0)
  const [monthReimbursableCount, setMonthReimbursableCount] = useState(0)
  const [monthPersonalTotal, setMonthPersonalTotal] = useState(0)
  const [monthPersonalCount, setMonthPersonalCount] = useState(0)

  // Year to date
  const [ytdReceiptCount, setYtdReceiptCount] = useState(0)
  const [ytdReceiptTotal, setYtdReceiptTotal] = useState(0)
  const [ytdMileage, setYtdMileage] = useState(0)
  const [ytdMileageDeduction, setYtdMileageDeduction] = useState(0)
  const [ytdCompanyCardTotal, setYtdCompanyCardTotal] = useState(0)
  const [ytdCompanyCardCount, setYtdCompanyCardCount] = useState(0)
  const [ytdReimbursableTotal, setYtdReimbursableTotal] = useState(0)
  const [ytdReimbursableCount, setYtdReimbursableCount] = useState(0)
  const [ytdPersonalTotal, setYtdPersonalTotal] = useState(0)
  const [ytdPersonalCount, setYtdPersonalCount] = useState(0)

  // Recent activity
  const [recentActivity, setRecentActivity] = useState([])

  // Mileage rate
  const [mileageRateCents, setMileageRateCents] = useState(70)

  useEffect(() => {
    loadDashboard()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadDashboard = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { setLoading(false); return }

      // Fetch ALL 4 default tags so we can show YTD breakdown by tag type
      const { data: allDefaultTags } = await supabase
        .from('tags')
        .select('id, tag_type')
        .eq('user_id', user.id)
        .eq('is_default', true)
        .in('tag_type', ['schedule_c', 'company_card', 'reimbursable', 'personal'])
      const tagIdByType = {}
      ;(allDefaultTags || []).forEach(t => { tagIdByType[t.tag_type] = t.id })
      const scheduleCTagId = tagIdByType.schedule_c || null
      const companyCardTagId = tagIdByType.company_card || null
      const reimbursableTagId = tagIdByType.reimbursable || null
      const personalTagId = tagIdByType.personal || null

      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, mileage_rate_cents')
        .eq('id', user.id)
        .single()

      if (profile) {
        setUserName(profile.full_name?.split(' ')[0] || user.email.split('@')[0])
        setMileageRateCents(profile.mileage_rate_cents || 70)
      } else {
        setUserName(user.email.split('@')[0])
      }

      const now = new Date()
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10)
      const yearStart = new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10)

      // Fetch ALL monthly receipts (no tag filter) so we can group by tag type
      const { data: monthAllReceipts } = await supabase
        .from('transactions')
        .select('amount, tag_id')
        .eq('user_id', user.id)
        .gte('transaction_date', monthStart)

      // Group monthly totals by tag type
      const monthTotals = { schedule_c: 0, company_card: 0, reimbursable: 0, personal: 0 }
      const monthCounts = { schedule_c: 0, company_card: 0, reimbursable: 0, personal: 0 }
      ;(monthAllReceipts || []).forEach(r => {
        const amt = parseFloat(r.amount || 0)
        if (r.tag_id === scheduleCTagId) { monthTotals.schedule_c += amt; monthCounts.schedule_c += 1 }
        else if (r.tag_id === companyCardTagId) { monthTotals.company_card += amt; monthCounts.company_card += 1 }
        else if (r.tag_id === reimbursableTagId) { monthTotals.reimbursable += amt; monthCounts.reimbursable += 1 }
        else if (r.tag_id === personalTagId) { monthTotals.personal += amt; monthCounts.personal += 1 }
      })

      setMonthReceiptCount(monthCounts.schedule_c)
      setMonthReceiptTotal(monthTotals.schedule_c)
      setMonthCompanyCardTotal(monthTotals.company_card)
      setMonthCompanyCardCount(monthCounts.company_card)
      setMonthReimbursableTotal(monthTotals.reimbursable)
      setMonthReimbursableCount(monthCounts.reimbursable)
      setMonthPersonalTotal(monthTotals.personal)
      setMonthPersonalCount(monthCounts.personal)

      const { data: monthTrips } = await supabase
        .from('mileage_trips')
        .select('miles, deduction_amount')
        .eq('user_id', user.id)
        .gte('trip_date', monthStart)

      const mMiles = (monthTrips || []).reduce((sum, tr) => sum + parseFloat(tr.miles || 0), 0)
      const mDeduct = (monthTrips || []).reduce((sum, tr) => sum + parseFloat(tr.deduction_amount || 0), 0)
      setMonthMileage(mMiles)
      setMonthMileageDeduction(mDeduct)

      // Fetch ALL YTD receipts for this user (no tag filter) so we can group by tag
      const { data: ytdAllReceipts } = await supabase
        .from('transactions')
        .select('amount, tag_id')
        .eq('user_id', user.id)
        .gte('transaction_date', yearStart)

      // Group totals by tag type
      const totalsByTag = { schedule_c: 0, company_card: 0, reimbursable: 0, personal: 0 }
      const countsByTag = { schedule_c: 0, company_card: 0, reimbursable: 0, personal: 0 }
      ;(ytdAllReceipts || []).forEach(r => {
        const amt = parseFloat(r.amount || 0)
        if (r.tag_id === scheduleCTagId) { totalsByTag.schedule_c += amt; countsByTag.schedule_c += 1 }
        else if (r.tag_id === companyCardTagId) { totalsByTag.company_card += amt; countsByTag.company_card += 1 }
        else if (r.tag_id === reimbursableTagId) { totalsByTag.reimbursable += amt; countsByTag.reimbursable += 1 }
        else if (r.tag_id === personalTagId) { totalsByTag.personal += amt; countsByTag.personal += 1 }
      })

      // Schedule C is the headline — keep existing variable names for compatibility
      setYtdReceiptCount(countsByTag.schedule_c)
      setYtdReceiptTotal(totalsByTag.schedule_c)
      // Set new state for breakdown
      setYtdCompanyCardTotal(totalsByTag.company_card)
      setYtdCompanyCardCount(countsByTag.company_card)
      setYtdReimbursableTotal(totalsByTag.reimbursable)
      setYtdReimbursableCount(countsByTag.reimbursable)
      setYtdPersonalTotal(totalsByTag.personal)
      setYtdPersonalCount(countsByTag.personal)

      const { data: ytdTrips } = await supabase
        .from('mileage_trips')
        .select('miles, deduction_amount')
        .eq('user_id', user.id)
        .gte('trip_date', yearStart)

      const yMiles = (ytdTrips || []).reduce((sum, tr) => sum + parseFloat(tr.miles || 0), 0)
      const yDeduct = (ytdTrips || []).reduce((sum, tr) => sum + parseFloat(tr.deduction_amount || 0), 0)
      setYtdMileage(yMiles)
      setYtdMileageDeduction(yDeduct)

      const { data: recentReceipts } = await supabase
        .from('transactions')
        .select('id, transaction_date, merchant, amount, receipt_url, receipt_file_type')
        .eq('user_id', user.id)
        .order('transaction_date', { ascending: false })
        .limit(5)

      const { data: recentTrips } = await supabase
        .from('mileage_trips')
        .select('id, trip_date, from_address, to_address, miles, deduction_amount, purpose')
        .eq('user_id', user.id)
        .order('trip_date', { ascending: false })
        .limit(5)

      const merged = [
        ...(recentReceipts || []).map(r => ({ ...r, _type: 'receipt', _date: r.transaction_date })),
        ...(recentTrips || []).map(tr => ({ ...tr, _type: 'trip', _date: tr.trip_date }))
      ].sort((a, b) => new Date(b._date) - new Date(a._date)).slice(0, 8)

      setRecentActivity(merged)
    } catch (err) {
      console.error('Dashboard load error:', err)
    }
    setLoading(false)
  }

  // Locale-aware formatters
  const lang = i18n.language || 'en'
  const locale = lang.startsWith('es') ? 'es-US' : 'en-US'
  const formatCurrency = (n) => new Intl.NumberFormat(locale, {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n || 0)
  const formatMiles = (n) => (n || 0).toLocaleString(locale, { maximumFractionDigits: 1 })
  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00')
    return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
  }
  const formatMonthYear = () => new Date().toLocaleDateString(locale, { month: 'long', year: 'numeric' })
  const formatMonth = () => new Date().toLocaleDateString(locale, { month: 'long' })

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.loadingText}>Merq<span style={styles.loadingAccent}>Log</span></div>
      </div>
    )
  }

  const totalMonthDeduction = monthReceiptTotal + monthMileageDeduction
  const totalYtdDeduction = ytdReceiptTotal + ytdMileageDeduction
  const hasAnyData = ytdReceiptCount > 0 || ytdMileage > 0

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.greeting}>
            {t('dashboard.greeting', { name: userName })} <span style={styles.wave}>👋</span>
          </h1>
          <p style={styles.subtitle}>
            {formatMonthYear()}
            {sub.trialActive && sub.daysRemaining !== null && (
              <>
                <span style={styles.dot}>·</span>
                <span style={sub.daysRemaining <= 3 ? styles.trialUrgent : styles.trialOk}>
                  {sub.daysRemaining === 0
                    ? t('dashboard.trial_ends_today')
                    : t('dashboard.trial_days_left', { count: sub.daysRemaining })}
                </span>
              </>
            )}
          </p>
        </div>
      </div>

      {/* Quick actions */}
      <div style={styles.quickActions}>
        <button style={styles.quickCard} onClick={() => navigate('/receipts?add=picker')}>
          <div style={styles.quickIcon}>🧾</div>
          <div style={styles.quickTitle}>{t('dashboard.add_receipt_title')}</div>
          <div style={styles.quickSub}>{t('dashboard.add_receipt_sub')}</div>
        </button>
        <button style={styles.quickCard} onClick={() => navigate('/mileage?add=1')}>
          <div style={styles.quickIcon}>🚗</div>
          <div style={styles.quickTitle}>{t('dashboard.log_trip_title')}</div>
          <div style={styles.quickSub}>{t('dashboard.log_trip_sub')}</div>
        </button>
      </div>

      {/* This month */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>{t('dashboard.this_month')}</h2>
          <span style={styles.sectionMonth}>{formatMonth()}</span>
        </div>

        <div style={styles.ytdGrid}>
          <div style={styles.ytdStat}>
            <span style={styles.ytdLabel}>{t('dashboard.tag_schedule_c')}</span>
            <span style={styles.ytdValue}>{monthReceiptCount}</span>
            <span style={styles.ytdSub}>{formatCurrency(monthReceiptTotal)}</span>
          </div>
          <div style={styles.ytdStat}>
            <span style={styles.ytdLabel}>{t('dashboard.miles')}</span>
            <span style={styles.ytdValue}>{formatMiles(monthMileage)}</span>
            <span style={styles.ytdSub}>{formatCurrency(monthMileageDeduction)}</span>
          </div>
          <div style={styles.ytdStat}>
            <span style={styles.ytdLabel}>{t('dashboard.tag_company_card')}</span>
            <span style={styles.ytdValue}>{monthCompanyCardCount}</span>
            <span style={styles.ytdSub}>{formatCurrency(monthCompanyCardTotal)}</span>
          </div>
          <div style={styles.ytdStat}>
            <span style={styles.ytdLabel}>{t('dashboard.tag_reimbursable')}</span>
            <span style={styles.ytdValue}>{monthReimbursableCount}</span>
            <span style={styles.ytdSub}>{formatCurrency(monthReimbursableTotal)}</span>
          </div>
          <div style={styles.ytdStat}>
            <span style={styles.ytdLabel}>{t('dashboard.tag_personal')}</span>
            <span style={styles.ytdValue}>{monthPersonalCount}</span>
            <span style={styles.ytdSub}>{formatCurrency(monthPersonalTotal)}</span>
          </div>
          <div style={styles.ytdStat}>
            <span style={styles.ytdLabel}>{t('dashboard.est_deduction')}</span>
            <span style={styles.ytdValue}>{formatCurrency(totalMonthDeduction)}</span>
            <span style={styles.ytdSub}>{t('dashboard.receipts_plus_mileage')}</span>
          </div>
        </div>
      </div>

      {/* Year to date */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>{t('dashboard.year_to_date')}</h2>
          <span style={styles.sectionMonth}>{new Date().getFullYear()}</span>
        </div>

        <div style={styles.ytdGrid}>
          <div style={styles.ytdStat}>
            <span style={styles.ytdLabel}>{t('dashboard.tag_schedule_c')}</span>
            <span style={styles.ytdValue}>{ytdReceiptCount}</span>
            <span style={styles.ytdSub}>{formatCurrency(ytdReceiptTotal)}</span>
          </div>
          <div style={styles.ytdStat}>
            <span style={styles.ytdLabel}>{t('dashboard.miles')}</span>
            <span style={styles.ytdValue}>{formatMiles(ytdMileage)}</span>
            <span style={styles.ytdSub}>{formatCurrency(ytdMileageDeduction)}</span>
          </div>
          <div style={styles.ytdStat}>
            <span style={styles.ytdLabel}>{t('dashboard.tag_company_card')}</span>
            <span style={styles.ytdValue}>{ytdCompanyCardCount}</span>
            <span style={styles.ytdSub}>{formatCurrency(ytdCompanyCardTotal)}</span>
          </div>
          <div style={styles.ytdStat}>
            <span style={styles.ytdLabel}>{t('dashboard.tag_reimbursable')}</span>
            <span style={styles.ytdValue}>{ytdReimbursableCount}</span>
            <span style={styles.ytdSub}>{formatCurrency(ytdReimbursableTotal)}</span>
          </div>
          <div style={styles.ytdStat}>
            <span style={styles.ytdLabel}>{t('dashboard.tag_personal')}</span>
            <span style={styles.ytdValue}>{ytdPersonalCount}</span>
            <span style={styles.ytdSub}>{formatCurrency(ytdPersonalTotal)}</span>
          </div>
          <div style={styles.ytdStat}>
            <span style={styles.ytdLabel}>{t('dashboard.est_deduction')}</span>
            <span style={styles.ytdValueAccent}>{formatCurrency(totalYtdDeduction)}</span>
            <span style={styles.ytdSub}>{t('dashboard.at_rate', { rate: (mileageRateCents / 100).toFixed(2) })}</span>
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>{t('dashboard.recent_activity')}</h2>
          <button style={styles.viewAllBtn} onClick={() => navigate('/receipts?add=picker')}>{t('dashboard.view_all')} →</button>
        </div>

        {recentActivity.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>🧾</div>
            <div style={styles.emptyTitle}>{t('dashboard.empty_title')}</div>
            <div style={styles.emptySub}>{t('dashboard.empty_sub')}</div>
            <div style={styles.emptyActions}>
              <button style={styles.primaryBtn} onClick={() => navigate('/receipts?add=scan')}>
                📷 {t('dashboard.empty_scan_btn')}
              </button>
              <button style={styles.secondaryBtn} onClick={() => navigate('/mileage?add=1')}>
                🚗 {t('dashboard.empty_log_btn')}
              </button>
            </div>
          </div>
        ) : (
          <div style={styles.activityList}>
            {recentActivity.map(item => (
              <div key={`${item._type}-${item.id}`} style={styles.activityRow}>
                <div style={styles.activityIcon}>
                  {item._type === 'receipt'
                    ? (item.receipt_file_type === 'pdf' ? '📄' : '🧾')
                    : '🚗'}
                </div>
                <div style={styles.activityMain}>
                  <div style={styles.activityTitle}>
                    {item._type === 'receipt'
                      ? (item.merchant || t('dashboard.activity_receipt_default'))
                      : `${item.from_address?.split(',')[0] || t('dashboard.activity_trip_default')} → ${item.to_address?.split(',')[0] || ''}`}
                  </div>
                  <div style={styles.activitySub}>
                    {formatDate(item._date)}
                    {item._type === 'trip' && item.purpose && ` · ${item.purpose}`}
                  </div>
                </div>
                <div style={styles.activityAmount}>
                  {item._type === 'receipt'
                    ? formatCurrency(item.amount)
                    : t('dashboard.activity_miles', { miles: formatMiles(item.miles) })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Trial footer CTA */}
      {sub.trialActive && sub.daysRemaining !== null && sub.daysRemaining <= 7 && hasAnyData && (
        <div style={styles.trialBanner}>
          <div style={styles.trialBannerTitle}>
            {sub.daysRemaining === 0
              ? t('dashboard.trial_ends_today')
              : t('dashboard.trial_days_left', { count: sub.daysRemaining })}
          </div>
          <div style={styles.trialBannerSub}>
            {t('dashboard.trial_keep_going')}
          </div>
          <button style={styles.trialBannerBtn} onClick={() => navigate('/settings')}>
            {t('dashboard.subscribe_now')} →
          </button>
        </div>
      )}
    </div>
  )
}

const teal = '#0D9488'
const tealLight = '#14B8A6'
const tealDark = '#0F766E'
const plum = '#1E1040'

const styles = {
  page: { padding: '24px 16px 80px', maxWidth: '1080px', margin: '0 auto', fontFamily: "'Plus Jakarta Sans', sans-serif", color: plum },
  loading: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' },
  loadingText: { fontFamily: "'Fraunces', serif", fontSize: '32px', fontWeight: 900, color: plum },
  loadingAccent: { color: teal, fontStyle: 'italic' },
  header: { marginBottom: '32px' },
  greeting: { fontFamily: "'Fraunces', serif", fontSize: '38px', fontWeight: 900, letterSpacing: '-1px', color: plum, margin: 0, lineHeight: 1.1 },
  wave: { fontFamily: "'Apple Color Emoji', sans-serif" },
  subtitle: { fontSize: '15px', color: '#6B7280', marginTop: '6px', fontWeight: 500 },
  dot: { margin: '0 8px', color: '#D1D5DB' },
  trialOk: { color: teal, fontWeight: 600 },
  trialUrgent: { color: '#DC2626', fontWeight: 700 },
  quickActions: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '40px' },
  quickCard: {
    background: '#fff', border: '1px solid #E5E7EB', borderRadius: '16px',
    padding: '22px 20px', textAlign: 'left', cursor: 'pointer',
    transition: 'all 0.2s', fontFamily: "'Plus Jakarta Sans', sans-serif",
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
  },
  quickIcon: { fontSize: '32px', marginBottom: '14px', lineHeight: 1, display: 'block' },
  quickTitle: { fontSize: '16px', fontWeight: 700, color: plum, marginBottom: '4px', display: 'block' },
  quickSub: { fontSize: '13px', color: '#6B7280', fontWeight: 500, display: 'block' },
  section: { marginBottom: '40px' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '16px' },
  sectionTitle: { fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 800, color: plum, letterSpacing: '-0.5px', margin: 0 },
  sectionMonth: { fontSize: '13px', color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
  viewAllBtn: { background: 'none', border: 'none', color: teal, fontSize: '14px', fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px' },
  statCard: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '16px', padding: '22px 20px' },
  statCardAccent: { background: `linear-gradient(135deg, ${teal}, ${tealLight})`, border: 'none', color: '#fff' },
  statLabel: { fontSize: '12px', color: '#6B7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' },
  statLabelAccent: { fontSize: '12px', color: 'rgba(255,255,255,0.85)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' },
  statValue: { fontFamily: "'Fraunces', serif", fontSize: '30px', fontWeight: 900, color: plum, lineHeight: 1, marginBottom: '6px' },
  statValueAccent: { fontFamily: "'Fraunces', serif", fontSize: '30px', fontWeight: 900, color: '#fff', lineHeight: 1, marginBottom: '6px' },
  statSub: { fontSize: '13px', color: '#6B7280', fontWeight: 500 },
  statSubAccent: { fontSize: '13px', color: 'rgba(255,255,255,0.9)', fontWeight: 500 },
  ytdGrid: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '16px', padding: '20px 24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '24px' },
  ytdStat: { display: 'flex', flexDirection: 'column', gap: '4px' },
  ytdLabel: { fontSize: '11px', color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' },
  ytdValue: { fontFamily: "'Fraunces', serif", fontSize: '26px', fontWeight: 800, color: plum, lineHeight: 1 },
  ytdValueAccent: { fontFamily: "'Fraunces', serif", fontSize: '26px', fontWeight: 800, color: teal, lineHeight: 1 },
  ytdSub: { fontSize: '12px', color: '#9CA3AF', fontWeight: 500, marginTop: '2px' },
  emptyState: { background: '#fff', border: '1px dashed #D1D5DB', borderRadius: '16px', padding: '48px 32px', textAlign: 'center' },
  emptyIcon: { fontSize: '48px', marginBottom: '12px' },
  emptyTitle: { fontFamily: "'Fraunces', serif", fontSize: '20px', fontWeight: 800, color: plum, marginBottom: '6px' },
  emptySub: { fontSize: '14px', color: '#6B7280', marginBottom: '24px', fontWeight: 500 },
  emptyActions: { display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' },
  primaryBtn: {
    background: teal, color: '#042f2e', border: 'none', borderRadius: '100px',
    padding: '11px 22px', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif", boxShadow: `0 4px 14px ${teal}40`,
  },
  secondaryBtn: {
    background: '#fff', color: plum, border: '1.5px solid #E5E7EB',
    borderRadius: '100px', padding: '11px 22px', fontSize: '14px', fontWeight: 600,
    cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  activityList: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '16px', overflow: 'hidden' },
  activityRow: { display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '14px', padding: '14px 20px', borderTop: '1px solid #F3F4F6', alignItems: 'center' },
  activityIcon: { fontSize: '22px', width: '32px', textAlign: 'center' },
  activityMain: { minWidth: 0 },
  activityTitle: { fontSize: '14px', fontWeight: 600, color: plum, marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  activitySub: { fontSize: '12px', color: '#9CA3AF', fontWeight: 500 },
  activityAmount: { fontFamily: "'Fraunces', serif", fontSize: '15px', fontWeight: 800, color: plum },
  trialBanner: {
    background: `linear-gradient(135deg, ${tealDark}, ${teal})`,
    borderRadius: '16px', padding: '24px 28px', color: '#fff',
    display: 'flex', flexDirection: 'column', gap: '6px',
    alignItems: 'flex-start', marginTop: '16px',
  },
  trialBannerTitle: { fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 800, letterSpacing: '-0.3px' },
  trialBannerSub: { fontSize: '14px', color: 'rgba(255,255,255,0.9)', fontWeight: 500, marginBottom: '10px' },
  trialBannerBtn: {
    background: '#fff', color: tealDark, border: 'none', borderRadius: '100px',
    padding: '10px 22px', fontSize: '14px', fontWeight: 800, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
}
